import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#657161', '#7A8B76', '#D7BD7B', '#BBD6D5', '#8FA68C', '#A3B89E', '#B8BDB6', '#6B7E67', '#999', '#5B8FC9', '#C97B5B'];

export default function ExpenseCategoriesPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#657161');

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/expense-categories/all`); setCategories(data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!newName.trim()) { toast.error('Nome obrigatório'); return; }
    await axios.post(`${API}/api/expense-categories`, { name: newName.trim(), color: newColor });
    toast.success('Categoria criada'); setNewName(''); fetch();
  };
  const rename = async (id, name) => { await axios.put(`${API}/api/expense-categories/${id}`, { name }); fetch(); };
  const changeColor = async (id, color) => { await axios.put(`${API}/api/expense-categories/${id}`, { color }); fetch(); };
  const archive = async (id) => { await axios.put(`${API}/api/expense-categories/${id}/archive`); toast.success('Categoria arquivada'); fetch(); };
  const reactivate = async (id) => { await axios.put(`${API}/api/expense-categories/${id}`, { is_active: true }); toast.success('Categoria reactivada'); fetch(); };
  const del = async (id) => {
    const c = categories.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a categoria de despesa "${c?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/expense-categories/${id}`); toast.success('Eliminada'); fetch(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const active = categories.filter(c => c.is_active !== false);
  const archived = categories.filter(c => c.is_active === false);

  return (
    <div className="fade-in" data-testid="expense-categories-page">
      <div className="mb-5">
        <button onClick={() => window.history.back()} className="text-sm text-brand-green mb-1 hover:underline">&larr; Voltar ao Financeiro</button>
        <h2 className="font-heading font-medium text-xl text-brand-text">Categorias de Despesa</h2>
      </div>

      {/* Add new */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Label className="text-xs text-brand-muted mb-1 block">Nova categoria</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome da categoria" className="rounded-xl border-brand-border" onKeyDown={e => { if (e.key === 'Enter') create(); }} /></div>
          <div className="flex gap-1">{COLORS.map(c => (<button key={c} onClick={() => setNewColor(c)} className={`w-6 h-6 rounded-full border-2 ${newColor === c ? 'border-brand-text' : 'border-transparent'}`} style={{ background: c }} />))}</div>
          <button onClick={create} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium"><Plus size={14} /></button>
        </div>
      </div>

      {/* Active categories */}
      <div className="space-y-2 mb-5">
        {active.map(c => (
          <div key={c.id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-3" data-testid={`cat-${c.id}`}>
            <div className="flex gap-1">{COLORS.map(col => (<button key={col} onClick={() => changeColor(c.id, col)} className={`w-4 h-4 rounded-full ${c.color === col ? 'ring-2 ring-brand-text ring-offset-1' : ''}`} style={{ background: col }} />))}</div>
            <div className="flex-1"><InlineEdit value={c.name} onSave={v => rename(c.id, v)} className="text-sm font-medium text-brand-text" /></div>
            <span className="text-[10px] text-brand-muted">{c.expense_count || 0} despesas</span>
            <button onClick={() => archive(c.id)} className="text-brand-placeholder hover:text-amber-500 p-1" title="Arquivar"><Archive size={14} /></button>
            {(c.expense_count || 0) === 0 && <button onClick={() => del(c.id)} className="text-brand-placeholder hover:text-red-500 p-1" title="Eliminar"><Trash size={14} /></button>}
          </div>
        ))}
      </div>

      {/* Archived */}
      {archived.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-brand-muted cursor-pointer">Arquivadas ({archived.length})</summary>
          <div className="space-y-1.5 mt-2">
            {archived.map(c => (
              <div key={c.id} className="bg-brand-bg/30 border border-brand-border rounded-xl p-3 flex items-center gap-2 opacity-60">
                <span className="w-3 h-3 rounded-full" style={{ background: c.color }} />
                <span className="text-xs text-brand-text flex-1">{c.name}</span>
                <button onClick={() => reactivate(c.id)} className="text-[10px] text-brand-green hover:underline flex items-center gap-1"><ArrowCounterClockwise size={12} /> Reativar</button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
